/**
 * PostSessionFeedbackModal — level-relative skill feedback.
 * Every skill at the player's current level gets a 0–5 slider, a "covered" tick,
 * a session-only rename and a coach-only note. Only covered skills feed AI + progression.
 * Pass `editFeedback` (a saved feedback doc) to edit an existing entry.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Sparkles, Send, Mail, Loader2, Plus, RotateCw, Pencil, Info, Flag, Lock, Undo2, CheckCircle2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const SCALE_NOTE = "Ratings are relative to the player's target level. 5 = fully mastered at this level.";

function LevelHeader({ meta }) {
  return (
    <div className="space-y-3" data-testid="feedback-level-header">
      <div className="flex flex-wrap items-center gap-3">
        <span className="px-3 py-1.5 text-sm font-bold uppercase tracking-wider bg-[#CCFF00] text-[#0A0A0A]" data-testid="feedback-current-level">
          {meta.sport} · {meta.level_label}
        </span>
        <span className="text-xs text-[#A3A3A3] inline-flex items-center gap-1.5" data-testid="feedback-scale-note">
          <Info className="w-3.5 h-3.5 text-[#66C2FF] shrink-0" /> {SCALE_NOTE}
        </span>
      </div>
      {meta.promoted_on_potential && meta.carry_forward_focus?.length > 0 && (
        <div className="border-l-2 border-[#FF9F1C] bg-[#1A1408] px-3 py-2" data-testid="feedback-carry-forward-note">
          <div className="text-xs font-bold text-[#FF9F1C] uppercase tracking-wider mb-1">
            Promoted on potential{meta.carried_from ? ` from ${meta.carried_from}` : ""} — {meta.carry_forward_focus.length} carry-forward focus area{meta.carry_forward_focus.length === 1 ? "" : "s"}
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
            {meta.carry_forward_focus.map((n) => <span key={n} className="px-2 py-0.5 text-[11px] border border-[#FF9F1C]/50 text-white">{n}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}

function SkillRow({ skill, entry, onChange, hideNote }) {
  const [renaming, setRenaming] = useState(false);
  const on = entry.covered;
  const shown = entry.label || skill.name;
  const k = skill.key;
  return (
    <div className={`border px-3 py-3 transition-opacity ${on ? "border-[#404040] bg-[#141414]" : "border-[#1F1F1F] bg-[#0F0F0F] opacity-50"}`}
      data-testid={`feedback-skill-row-${k}`} data-covered={on ? "true" : "false"}>
      <div className="flex items-start gap-3">
        <input type="checkbox" checked={on} onChange={(e) => onChange({ covered: e.target.checked })}
          className="mt-1 w-4 h-4 accent-[#CCFF00] shrink-0" data-testid={`skill-covered-${k}`} aria-label={`Covered ${shown}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {renaming ? (
              <input autoFocus value={entry.label || skill.name} onChange={(e) => onChange({ label: e.target.value })}
                onBlur={() => setRenaming(false)} onKeyDown={(e) => e.key === "Enter" && setRenaming(false)}
                className="bg-[#0A0A0A] border border-[#CCFF00] px-2 py-0.5 text-sm focus:outline-none" data-testid={`skill-rename-input-${k}`} />
            ) : (
              <span className="text-sm font-semibold" title={skill.criteria || ""} data-testid={`skill-label-${k}`}>{shown}</span>
            )}
            <button type="button" onClick={() => setRenaming(true)} className="text-[#A3A3A3] hover:text-[#CCFF00]"
              title="Rename for this session only" data-testid={`skill-rename-${k}`}><Pencil className="w-3 h-3" /></button>
            {entry.label && entry.label !== skill.name && (
              <button type="button" onClick={() => onChange({ label: "" })} className="text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
                data-testid={`skill-rename-reset-${k}`}><Undo2 className="w-3 h-3" /> {skill.name}</button>
            )}
            {skill.carry_forward && <span className="text-[10px] uppercase tracking-wider text-[#FF9F1C] inline-flex items-center gap-1" data-testid={`skill-carry-${k}`}><Flag className="w-3 h-3" /> Focus area</span>}
            {skill.focus_area && !skill.carry_forward && <span className="text-[10px] uppercase tracking-wider text-[#FF9F1C] inline-flex items-center gap-1" data-testid={`skill-flagged-${k}`}><Flag className="w-3 h-3" /> Flagged focus area — note progress</span>}
            {skill.custom && <span className="text-[10px] uppercase tracking-wider text-[#66C2FF]">Custom</span>}
          </div>
          <div className="flex items-center gap-3 mt-2">
            <input type="range" min={0} max={5} step={0.5} value={entry.rating} disabled={!on}
              onChange={(e) => onChange({ rating: Number(e.target.value) })}
              className="flex-1 accent-[#CCFF00] disabled:cursor-not-allowed" data-testid={`skill-slider-${k}`} title={SCALE_NOTE} />
            <span className="w-10 text-right text-sm font-mono text-[#CCFF00]" data-testid={`skill-value-${k}`}>{entry.rating}/5</span>
          </div>
          {!hideNote && (
            <textarea rows={1} value={entry.coach_note} disabled={!on} onChange={(e) => onChange({ coach_note: e.target.value })}
              placeholder="Coach-only note — never shown to the player"
              className="mt-2 w-full bg-[#0A0A0A] border border-[#262626] px-2 py-1.5 text-xs focus:outline-none focus:border-[#CCFF00] resize-y disabled:cursor-not-allowed"
              data-testid={`skill-note-${k}`} />
          )}
        </div>
      </div>
    </div>
  );
}

function AddCustomSkill({ meta, onAdded }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/coach/custom-skills", { sport: meta.sport, level: meta.level, name: name.trim() });
      onAdded(data.skill);
      setName("");
      toast.success(`Added to your ${meta.level_label} skills`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not add skill"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2" data-testid="custom-skill-add-wrap">
      <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        placeholder={`Add a skill to your ${meta.level_label} list (saved for all your players)`}
        className="flex-1 bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00]" data-testid="custom-skill-input" />
      <button type="button" onClick={add} disabled={busy || !name.trim()} data-testid="custom-skill-add"
        className="px-3 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5 disabled:opacity-40">
        <Plus className="w-3.5 h-3.5" /> Add skill
      </button>
    </div>
  );
}

function Recap({ saved }) {
  return (
    <div className="bg-[#141414] border border-[#CCFF00] p-4">
      <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-[#CCFF00]" /><div className="label-mono text-[#CCFF00]">AI RECAP</div></div>
      {saved.ai_summary
        ? <p className="text-sm text-[#E5E5E5] whitespace-pre-wrap" data-testid="feedback-ai-summary">{saved.ai_summary}</p>
        : <p className="text-sm text-[#A3A3A3] italic">AI summary unavailable — ratings saved.</p>}
      {[["Positive highlights", saved.positive_highlights], ["Focus areas next", saved.focus_areas_for_improvement]].map(([t, list]) => (list || []).length > 0 && (
        <div className="mt-3" key={t}>
          <div className="label-mono text-[#A3A3A3] mb-1">{t}</div>
          <ul className="text-xs text-[#E5E5E5] list-disc pl-4 space-y-0.5">{list.map((h, i) => <li key={i}>{h}</li>)}</ul>
        </div>
      ))}
    </div>
  );
}

const seedEntries = (skills, source) => {
  const byKey = Object.fromEntries((source || []).map((e) => [e.key, e]));
  return Object.fromEntries(skills.map((s) => {
    const e = byKey[s.key];
    return [s.key, e
      ? { covered: !!e.covered, rating: Number(e.rating) || 0, coach_note: e.coach_note || "", label: e.label || "" }
      : { covered: false, rating: Number(s.rating) || 0, coach_note: "", label: "" }];
  }));
};

export default function PostSessionFeedbackModal({
  open, onClose, onSaved, bookingId, studentId, studentName = "", sessionTitle = "",
  sport = "pickleball", prefill = null, editFeedback = null, teamStudentIds = null, afterSave = null,
}) {
  const [meta, setMeta] = useState(null);
  const [entries, setEntries] = useState({});
  const [generalNote, setGeneralNote] = useState("");
  const [saved, setSaved] = useState(null);
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState("");
  const [dupWarn, setDupWarn] = useState(false);
  const [justShared, setJustShared] = useState(false);
  const isTeam = !!(teamStudentIds && teamStudentIds.length > 1);
  const fbSport = editFeedback?.sport || sport;

  useEffect(() => {
    if (!open) return;
    setSaved(editFeedback || null); setStep("form"); setMeta(null); setBusy(""); setDupWarn(false); setJustShared(false);
    const source = editFeedback || prefill;
    setGeneralNote(source?.general_notes || "");
    api.get(`/coach/players/${studentId}/feedback-skills`, { params: { sport: fbSport, booking_id: bookingId || "" } }).then(({ data }) => {
      const known = new Set(data.skills.map((s) => s.key));
      const extra = (source?.skill_entries || []).filter((e) => !known.has(e.key))
        .map((e) => ({ key: e.key, name: e.name, group: e.group, custom: e.custom, carry_forward: e.carry_forward, rating: e.rating }));
      const skills = [...data.skills, ...extra];
      setMeta({ ...data, skills });
      setEntries(seedEntries(skills, source?.skill_entries));
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load this player's skills"));
  }, [open, studentId, fbSport, editFeedback, prefill, bookingId]);

  const coveredCount = useMemo(() => Object.values(entries).filter((e) => e.covered).length, [entries]);
  const update = (key, patch) => setEntries((p) => ({ ...p, [key]: { ...p[key], ...patch } }));

  const payloadEntries = () => meta.skills.map((s) => ({
    key: s.key, name: s.name, group: s.group, custom: !!s.custom, carry_forward: !!s.carry_forward,
    ...entries[s.key], label: (entries[s.key].label || "").trim() === s.name ? "" : (entries[s.key].label || "").trim(),
  }));

  const doSubmit = async () => {
    setDupWarn(false);
    setBusy("save");
    const body = { skill_entries: payloadEntries(), general_notes: generalNote.trim(), sport: meta.sport };
    try {
      let fb;
      if (saved?.id && !isTeam) {
        fb = (await api.put(`/students/${studentId}/post-session-feedback/${saved.id}/skills`, body)).data.feedback;
      } else if (isTeam) {
        const r = await api.post("/coach/team-feedback", { ...body, session_id: bookingId, student_ids: teamStudentIds });
        fb = { id: r.data?.batch_id, ai_summary: `Shared recap sent to ${r.data?.successes} player(s).`, team: true, team_count: r.data?.successes };
      } else {
        fb = (await api.post(`/students/${studentId}/post-session-feedback`, { ...body, session_id: bookingId, reused_from: prefill?.reused_from || null })).data.feedback;
      }
      setSaved(fb); setStep("recap");
      const msg = sharedMsg(fb, !!saved?.id);
      setJustShared(msg);
      toast.success(msg);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed to save feedback"); }
    finally { setBusy(""); }
  };

  const sharedMsg = (fb, updated) => fb?.team
    ? `Feedback shared with ${fb.team_count || teamStudentIds.length} players.`
    : `${updated ? "Updated feedback" : "Feedback"} shared with ${studentName || "the player"}.`;

  const submit = async () => {
    if (!coveredCount && !generalNote.trim()) { toast.error("Tick at least one skill as covered or add a general note"); return; }
    if (!saved?.id && bookingId) {
      try {
        const ids = isTeam ? teamStudentIds : [studentId];
        const { data } = await api.get("/coach/feedback-exists", { params: { session_id: bookingId, student_ids: ids.join(",") } });
        if (data.existing?.length) { setDupWarn(true); return; }
      } catch { /* fall through — never block saving on a failed check */ }
    }
    doSubmit();
  };

  const emailPlayer = async () => {
    setBusy("email");
    try { await api.post(`/students/${studentId}/post-session-feedback/${saved.id}/email`); toast.success("Recap emailed to player"); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Email failed"); }
    finally { setBusy(""); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="post-session-feedback-modal" role="dialog" aria-modal="true">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-[#262626] p-6 relative">
        <button type="button" onClick={onClose} data-testid="feedback-modal-close" className="absolute top-3 right-3 text-[#A3A3A3] hover:text-white" aria-label="Close"><X className="w-5 h-5" /></button>
        <div className="mb-5">
          <div className="label-mono text-[#CCFF00] mb-1">SESSION FEEDBACK · {step === "recap" ? "SAVED" : saved?.id ? "EDITING" : "NEW"}</div>
          <h2 className="h-display text-2xl mb-1">{studentName || "Player"}</h2>
          {sessionTitle && <div className="text-xs text-[#A3A3A3]">{sessionTitle}</div>}
          {afterSave && <div className="mt-3">{afterSave}</div>}
        </div>

        {step === "form" && !meta && <div className="flex items-center gap-2 text-sm text-[#A3A3A3]"><Loader2 className="w-4 h-4 animate-spin" /> Loading skills…</div>}

        {step === "form" && meta && (
          <div className="space-y-5" data-testid="feedback-form">
            <LevelHeader meta={meta} />
            <div className="flex items-center justify-between">
              <div className="label-mono text-[#A3A3A3]">Skills · tick what you covered this session</div>
              <div className="text-xs text-[#CCFF00]" data-testid="feedback-covered-count">{coveredCount} covered</div>
            </div>
            <div className="space-y-2" data-testid="feedback-skill-list">
              {meta.skills.map((s) => <SkillRow key={s.key} skill={s} entry={entries[s.key]} hideNote={meta.is_assessment} onChange={(p) => update(s.key, p)} />)}
            </div>
            <AddCustomSkill meta={meta} onAdded={(sk) => { setMeta((m) => ({ ...m, skills: [...m.skills, sk] })); setEntries((p) => ({ ...p, [sk.key]: { covered: false, rating: 0, coach_note: "", label: "" } })); }} />
            <div>
              <div className="label-mono text-[#A3A3A3] mb-1">General note</div>
              <textarea rows={3} value={generalNote} onChange={(e) => setGeneralNote(e.target.value)} data-testid="feedback-general-note"
                placeholder="Overall session summary, mindset, next steps…"
                className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:outline-none focus:border-[#CCFF00] resize-y" />
              <div className="text-[11px] text-[#737373] mt-1 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Per-skill notes stay with you. The player only sees the AI recap.</div>
            </div>
            {dupWarn && (
              <div ref={(el) => el?.scrollIntoView({ behavior: "smooth", block: "center" })} className="p-3 border border-[#FF9F1C]/60 bg-[#FF9F1C]/10 space-y-3" role="alertdialog" data-testid="feedback-duplicate-warning">
                <div className="flex gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-[#FF9F1C] mt-0.5" />
                  <span>Feedback for this session has already been shared with {isTeam ? "at least one of these players" : "the player"}. Are you sure you want to add another entry?</span>
                </div>
                <div className="flex justify-end gap-2">
                  <button type="button" onClick={() => setDupWarn(false)} data-testid="feedback-duplicate-cancel"
                    className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white">Cancel</button>
                  <button type="button" onClick={doSubmit} data-testid="feedback-duplicate-confirm"
                    className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#FF9F1C] text-black">Add another entry</button>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-3 border-t border-[#262626]">
              <button type="button" onClick={saved?.id ? () => setStep("recap") : onClose} className="px-4 py-2 text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-white" data-testid="feedback-cancel">Cancel</button>
              <button type="button" onClick={submit} disabled={!!busy || dupWarn} data-testid="feedback-submit"
                className="btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-40" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
                {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {busy === "save" ? "Saving…" : saved?.id ? "Save changes" : "Submit feedback"}
              </button>
            </div>
          </div>
        )}

        {step === "recap" && saved && (
          <div className="space-y-4" data-testid="feedback-step-3">
            {justShared && (
              <div className="flex items-center gap-2 p-3 border border-[#CCFF00]/60 bg-[#CCFF00]/10 text-sm font-semibold text-[#CCFF00]" data-testid="feedback-shared-banner">
                <CheckCircle2 className="w-4 h-4 shrink-0" /> {justShared}
              </div>
            )}
            <Recap saved={saved} />
            <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-[#262626]">
              {!saved.team && (
                <button type="button" onClick={() => setStep("form")} data-testid="feedback-edit"
                  className="px-4 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> Edit ratings & notes
                </button>
              )}
              {!saved.team && (
                <button type="button" onClick={submit} disabled={!!busy} data-testid="feedback-regenerate"
                  className="px-4 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-1.5 disabled:opacity-40">
                  {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />} Redo AI summary
                </button>
              )}
              {!saved.team && (
                <button type="button" onClick={emailPlayer} disabled={!!busy} data-testid="feedback-email"
                  className="btn-volt px-5 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-40" style={{ background: "#CCFF00", color: "#0A0A0A" }}>
                  {busy === "email" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-3.5 h-3.5" />} Email to player
                </button>
              )}
              <button type="button" onClick={onClose} data-testid="feedback-done" className="px-4 py-2 text-xs uppercase tracking-wider border border-[#404040] hover:border-white hover:text-white">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
